"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import logo from "@/imports/Rann-Utsav-Logo-scaled_cr.png";

export default function Preloader() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    // Basic structural fallback for initial server-render to avoid layout shift
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#070518]">
        <div className="w-16 h-16 rounded-full border-4 border-yellow-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  // Pre-generate stars for standard starry sky
  const starsArray = [
    { top: "10%", left: "15%", delay: "0.2s", size: "2px" },
    { top: "25%", left: "80%", delay: "0.8s", size: "3px" },
    { top: "15%", left: "45%", delay: "1.5s", size: "1.5px" },
    { top: "40%", left: "10%", delay: "2.3s", size: "2.5px" },
    { top: "35%", left: "70%", delay: "0.5s", size: "2px" },
    { top: "50%", left: "90%", delay: "1.8s", size: "3px" },
    { top: "60%", left: "20%", delay: "2.9s", size: "1.5px" },
    { top: "70%", left: "85%", delay: "1.1s", size: "2.5px" },
    { top: "80%", left: "40%", delay: "0.7s", size: "2px" },
    { top: "18%", left: "65%", delay: "2.1s", size: "2px" },
    { top: "55%", left: "5%", delay: "1.4s", size: "3px" },
    { top: "75%", left: "60%", delay: "0.4s", size: "1.5px" },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-br from-[#0a071c] via-[#05030f] to-[#120925] overflow-hidden select-none">
      {/* 1. Starry Sky Layer */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {starsArray.map((star, idx) => (
          <div
            key={idx}
            className="absolute bg-white rounded-full animate-star-shimmer shadow-[0_0_8px_rgba(255,255,255,0.8)]"
            style={{
              top: star.top,
              left: star.left,
              width: star.size,
              height: star.size,
              animationDelay: star.delay,
            }}
          />
        ))}
      </div>

      {/* 2. Soft Glowing Desert Moon (Upper Right) */}
      <div className="absolute top-[8%] right-[10%] w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-yellow-100 via-orange-100 to-transparent opacity-95 blur-[1px] animate-moon-float shadow-[0_0_50px_rgba(253,224,71,0.25)] flex items-center justify-center">
        {/* Moon details / craters */}
        <div className="absolute top-[25%] left-[20%] w-[18%] h-[18%] rounded-full bg-amber-200/25 blur-[1px]" />
        <div className="absolute top-[50%] left-[45%] w-[12%] h-[12%] rounded-full bg-amber-200/20 blur-[1px]" />
        <div className="absolute top-[35%] left-[60%] w-[15%] h-[15%] rounded-full bg-amber-200/25 blur-[1px]" />
      </div>

      {/* 3. Center Preloader Core */}
      <div className="relative flex flex-col items-center justify-center px-4">
        {/* Glowing aura background */}
        <div className="absolute w-72 h-72 sm:w-96 sm:h-96 rounded-full bg-gradient-to-r from-yellow-500/10 via-pink-500/10 to-purple-600/10 blur-[80px] pointer-events-none" />

        {/* Rotating Kutchi Mandala / Chakra representation */}
        <div className="relative w-48 h-48 sm:w-60 sm:h-60 flex items-center justify-center">
          <svg
            viewBox="0 0 200 200"
            className="w-full h-full text-pink-500/60 animate-kutchi-spin"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer dotted rings */}
            <circle
              cx="100"
              cy="100"
              r="95"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeDasharray="3 6"
              className="text-purple-500/40"
            />
            <circle
              cx="100"
              cy="100"
              r="88"
              fill="none"
              stroke="url(#kutchi-grad)"
              strokeWidth="2.5"
            />

            {/* Concentric diamond petals (Traditional Kutchi Embroidery style) */}
            <g fill="none" stroke="url(#kutchi-grad)" strokeWidth="1.5">
              {[...Array(16)].map((_, i) => {
                const angle = (i * 360) / 16;
                return (
                  <path
                    key={i}
                    d="M100 12 L106 35 L100 55 L94 35 Z"
                    transform={`rotate(${angle} 100 100)`}
                  />
                );
              })}
            </g>

            {/* Inner Ring with mirror shapes */}
            <circle
              cx="100"
              cy="100"
              r="55"
              fill="none"
              stroke="url(#kutchi-grad)"
              strokeWidth="1.5"
              strokeDasharray="6 4"
            />
            <g fill="currentColor" className="text-yellow-400/80">
              {[...Array(8)].map((_, i) => {
                const angle = (i * 360) / 8 + 22.5;
                return (
                  <circle
                    key={i}
                    cx="100"
                    cy="45"
                    r="3.5"
                    transform={`rotate(${angle} 100 100)`}
                    className="shadow-[0_0_5px_rgba(250,204,21,0.8)]"
                  />
                );
              })}
            </g>

            {/* Core definition gradients */}
            <defs>
              <linearGradient
                id="kutchi-grad"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#eab308" />
                <stop offset="50%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
          </svg>

          {/* Centered pulsing Logo container */}
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-white p-2.5 shadow-[0_15px_35px_rgba(0,0,0,0.4)] flex items-center justify-center overflow-hidden border-2 border-pink-500/30 animate-pulse-glow">
            <div className="relative w-full h-full rounded-full overflow-hidden">
              <Image
                src={logo}
                alt="Rann Utsav Loader"
                fill
                priority
                className="object-contain p-1"
              />
            </div>
          </div>
        </div>

        {/* 4. Elegant Text & Slogan */}
        <div className="mt-8 text-center space-y-2.5 z-10">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-wider font-sans text-white">
            Chalo{" "}
            <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 bg-clip-text text-transparent">
              Kutch
            </span>
          </h2>
          <div className="flex items-center justify-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-ping" />
            <span className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-ping delay-200" />
            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-ping delay-500" />
            <p className="text-xs sm:text-sm font-light tracking-widest text-slate-300 font-sans pl-1">
              EXPERIENCING THE WHITE DESERT MAGIC
            </p>
          </div>
        </div>
      </div>

      {/* 5. Scenic Dunes & Camel Silhouette (Bottom) */}
      <div className="absolute bottom-0 left-0 right-0 w-full h-24 sm:h-32 pointer-events-none overflow-hidden select-none">
        {/* Back Dune Line */}
        <svg
          viewBox="0 0 1440 200"
          preserveAspectRatio="none"
          className="absolute bottom-0 w-full h-[85%] text-[#140b2a] fill-current opacity-80 translate-x-[-1%] scale-x-[1.02] animate-dune-wave"
        >
          <path d="M0,120 C360,180 720,80 1080,140 C1260,170 1380,130 1440,110 L1440,200 L0,200 Z" />
        </svg>

        {/* Front Dune Line */}
        <svg
          viewBox="0 0 1440 200"
          preserveAspectRatio="none"
          className="absolute bottom-0 w-full h-[65%] text-[#0d071d] fill-current"
        >
          <path d="M0,150 C240,110 480,180 720,130 C960,80 1200,160 1440,125 L1440,200 L0,200 Z" />
        </svg>

        {/* Camel Caravan Silhouette Walking across the Dune */}
        <div className="absolute bottom-[20%] left-[10%] sm:left-[20%] w-24 sm:w-28 h-auto opacity-75 z-10 animate-moon-float">
          <svg
            viewBox="0 0 100 80"
            fill="currentColor"
            className="w-full h-full text-yellow-500/60"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Stylized Camel Silhouette */}
            <path d="M20,60 C23,60 25,58 24,55 C23,50 20,44 23,38 C26,32 32,30 35,32 C38,34 38,38 41,39 C44,40 48,34 50,30 C52,26 56,16 59,10 C61,6 64,2 67,4 C70,6 68,14 67,18 C66,22 65,26 69,28 C73,30 76,28 78,32 C80,36 78,41 75,44 C72,47 70,52 74,56 C78,60 84,60 84,63 C84,65 80,66 77,63 C75,61 74,59 70,58 C66,57 63,59 60,60 C57,61 54,63 50,62 C46,61 44,57 41,56 C38,55 35,57 32,58 C28,59 25,60 20,60 Z" />
            <circle cx="21" cy="58" r="1.5" />
            {/* Camel Legs */}
            <path d="M30,59 L29,74 L27,74 L29,59 Z" />
            <path d="M36,58 L37,73 L39,73 L37,58 Z" />
            <path d="M62,60 L61,75 L59,75 L61,60 Z" />
            <path d="M70,58 L72,73 L74,73 L72,58 Z" />
          </svg>
        </div>

        {/* Small second camel trailing behind */}
        <div className="absolute bottom-[16%] left-[6%] sm:left-[15%] w-14 sm:w-16 h-auto opacity-50 z-10 animate-moon-float delay-500">
          <svg
            viewBox="0 0 100 80"
            fill="currentColor"
            className="w-full h-full text-yellow-600/50"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M20,60 C23,60 25,58 24,55 C23,50 20,44 23,38 C26,32 32,30 35,32 C38,34 38,38 41,39 C44,40 48,34 50,30 C52,26 56,16 59,10 C61,6 64,2 67,4 C70,6 68,14 67,18 C66,22 65,26 69,28 C73,30 76,28 78,32 C80,36 78,41 75,44 C72,47 70,52 74,56 C78,60 84,60 84,63 C84,65 80,66 77,63 C75,61 74,59 70,58 C66,57 63,59 60,60 C57,61 54,63 50,62 C46,61 44,57 41,56 C38,55 35,57 32,58 C28,59 25,60 20,60 Z" />
            <path d="M30,59 L29,74 L27,74 L29,59 Z" />
            <path d="M36,58 L37,73 L39,73 L37,58 Z" />
            <path d="M62,60 L61,75 L59,75 L61,60 Z" />
            <path d="M70,58 L72,73 L74,73 L72,58 Z" />
          </svg>
        </div>
      </div>
    </div>
  );
}
